'use client';

import { useCallback, useRef, useEffect } from 'react';
import { useToastStore } from '../model/toast-store';
import { useRealtimeAgentStore } from '@/shared/store/realtime-agent';
import { appendStreamingText, stripEmotionTagsForDisplay } from '@/shared/lib/emotion-tags';

interface TtsToastInfo {
  text: string;
  rawText: string;
  toastId: string;
  timeoutId: NodeJS.Timeout | null;
  totalAudioDuration: number;
}

/**
 * TTS 토스트 훅
 * AI 응답을 자막 형태로 표시 (타이핑 효과 + 오디오 기반 자동 닫기)
 *
 * 동작:
 * 1. 첫 텍스트 청크 도착 시 토스트 생성
 * 2. 후속 텍스트 청크마다 메시지 누적
 * 3. 오디오 duration 누적 후 (총 duration + 1초) 후 자동 닫기
 */
export const useTtsToast = (currentMode: 'voice' | 'chat') => {
  const ttsToastsRef = useRef<Map<string, TtsToastInfo>>(new Map());
  const processedChunksRef = useRef<Set<string>>(new Set());
  const currentTurnIdRef = useRef<string | null>(null);

  const showToast = useToastStore((s) => s.showToast);
  const hideToast = useToastStore((s) => s.hideToast);
  const updateToastMessage = useToastStore((s) => s.updateToastMessage);

  /**
   * 모든 TTS 토스트 강제 정리
   */
  const clearAllTtsToasts = useCallback(() => {
    console.log('🎬 [TTS-TOAST] 모든 토스트 정리, 개수:', ttsToastsRef.current.size);
    ttsToastsRef.current.forEach((info) => {
      if (info.timeoutId) clearTimeout(info.timeoutId);
      hideToast(info.toastId);
    });
    ttsToastsRef.current.clear();
    processedChunksRef.current.clear();
  }, [hideToast]);

  /**
   * 텍스트 청크 처리
   */
  const handleTextChunk = useCallback(
    (turnId: string, text: string, sequenceNum: number) => {
      console.log('🎬 [TTS-TOAST] handleTextChunk 호출:', { turnId, text: text?.substring(0, 20), sequenceNum });

      // 새로운 턴이 시작되면 이전 토스트 모두 정리
      if (currentTurnIdRef.current && currentTurnIdRef.current !== turnId) {
        console.log('🎬 [TTS-TOAST] 새 턴 시작, 이전 토스트 정리:', currentTurnIdRef.current, '->', turnId);
        clearAllTtsToasts();
      }
      currentTurnIdRef.current = turnId;

      // 중복 방지
      const chunkKey = `${turnId}-${sequenceNum}`;
      if (processedChunksRef.current.has(chunkKey)) {
        console.log('🎬 [TTS-TOAST] 중복 청크, 스킵:', chunkKey);
        return;
      }
      processedChunksRef.current.add(chunkKey);

      if (!ttsToastsRef.current.has(turnId)) {
        // 첫 청크: 새 토스트 생성
        console.log('🎬 [TTS-TOAST] 첫 청크, 토스트 생성');
        const displayText = stripEmotionTagsForDisplay(text);
        const toastId = showToast({
          type: 'tts',
          message: displayText,
          position: 'bottom',
          duration: 0, // 수동 해제
        });
        console.log('🎬 [TTS-TOAST] 토스트 생성됨, toastId:', toastId);

        ttsToastsRef.current.set(turnId, {
          text: displayText,
          rawText: text,
          toastId,
          timeoutId: null,
          totalAudioDuration: 0,
        });
      } else {
        // 후속 청크: 텍스트 누적
        const info = ttsToastsRef.current.get(turnId)!;
        info.rawText = appendStreamingText(info.rawText, text);
        info.text = stripEmotionTagsForDisplay(info.rawText);
        console.log('🎬 [TTS-TOAST] 텍스트 누적:', info.text?.substring(0, 50));
        updateToastMessage(info.toastId, info.text);
      }
    },
    [showToast, updateToastMessage, clearAllTtsToasts]
  );

  /**
   * 오디오 청크 처리 (duration 누적 및 타이머 갱신)
   */
  const handleAudioChunk = useCallback(
    (turnId: string, audioDuration: number) => {
      const info = ttsToastsRef.current.get(turnId);
      if (!info) return;

      info.totalAudioDuration += audioDuration;
      const dismissDelay = info.totalAudioDuration * 1000 + 1000;

      // 기존 타이머 취소 후 새 타이머 설정
      if (info.timeoutId) clearTimeout(info.timeoutId);
      info.timeoutId = setTimeout(() => {
        hideToast(info.toastId);
        ttsToastsRef.current.delete(turnId);
      }, dismissDelay);
    },
    [hideToast]
  );

  /**
   * 턴 종료 처리
   */
  const handleTurnEnd = useCallback(
    (turnId: string) => {
      const info = ttsToastsRef.current.get(turnId);
      if (!info) return;

      // 오디오 없으면 3초 후 닫기
      if (info.totalAudioDuration === 0) {
        const timeoutId = setTimeout(() => {
          hideToast(info.toastId);
          ttsToastsRef.current.delete(turnId);
        }, 3000);
        info.timeoutId = timeoutId;
      }

      // 처리된 청크 정보 클리어 (메모리 관리)
      processedChunksRef.current.clear();
    },
    [hideToast]
  );

  // 채팅 모드로 전환 시 모든 TTS 토스트 정리
  useEffect(() => {
    if (currentMode === 'chat') {
      console.log('🎬 [TTS-TOAST] 채팅 모드 전환 - 모든 토스트 정리')
      clearAllTtsToasts()
      currentTurnIdRef.current = null
    }
  }, [currentMode, clearAllTtsToasts])

  // interrupt 상태 및 턴 초기화 감지 → 토스트 정리
  useEffect(() => {
    if (currentMode !== 'voice') return;

    // isInterrupted 구독
    const unsubInterrupt = useRealtimeAgentStore.subscribe(
      (state) => state.isInterrupted,
      (isInterrupted) => {
        if (isInterrupted) {
          console.log('🎬 [TTS-TOAST] Interrupt 감지, 토스트 정리');
          clearAllTtsToasts();
          currentTurnIdRef.current = null;
        }
      }
    );

    // currentTurnId가 null이 되면 토스트 정리 (턴 초기화)
    const unsubTurnId = useRealtimeAgentStore.subscribe(
      (state) => state.currentTurnId,
      (turnId, prevTurnId) => {
        if (!turnId && prevTurnId) {
          console.log('🎬 [TTS-TOAST] 턴 초기화 감지, 토스트 정리');
          clearAllTtsToasts();
          currentTurnIdRef.current = null;
        }
      }
    );

    return () => {
      unsubInterrupt();
      unsubTurnId();
    };
  }, [currentMode, clearAllTtsToasts]);

  // currentChunk 구독 (CHUNK 이벤트에서 텍스트 + 오디오 처리)
  // Voice Server는 TEXT_CHUNK가 아닌 CHUNK 이벤트로 텍스트를 전달
  useEffect(() => {
    console.log('🎬 [TTS-TOAST] useEffect 실행, currentMode:', currentMode);

    if (currentMode !== 'voice') {
      console.log('🎬 [TTS-TOAST] voice 모드 아님, 구독 스킵');
      return;
    }

    console.log('🎬 [TTS-TOAST] currentChunk 구독 시작');

    const unsubscribe = useRealtimeAgentStore.subscribe(
      (state) => state.currentChunk,
      (currentChunk) => {
        console.log('🎬 [TTS-TOAST] currentChunk 변경:', currentChunk === 'END' ? 'END' : currentChunk?.sequence_num);

        if (!currentChunk) return;

        if (currentChunk === 'END') {
          // 턴 종료
          const currentTurnId = useRealtimeAgentStore.getState().currentTurnId;
          console.log('🎬 [TTS-TOAST] 턴 종료, turnId:', currentTurnId);
          if (currentTurnId) {
            handleTurnEnd(currentTurnId);
          }
        } else {
          // CHUNK 이벤트에서 텍스트 추출하여 토스트 표시
          if (currentChunk.text) {
            console.log('🎬 [TTS-TOAST] 청크 텍스트:', currentChunk.turn_id, currentChunk.text?.substring(0, 30));
            handleTextChunk(currentChunk.turn_id, currentChunk.text, currentChunk.sequence_num);
          }

          // 오디오 duration 처리
          if (currentChunk.audio && currentChunk.lipsync?.duration) {
            handleAudioChunk(currentChunk.turn_id, currentChunk.lipsync.duration);
          }
        }
      }
    );

    return () => {
      console.log('🎬 [TTS-TOAST] currentChunk 구독 해제');
      unsubscribe();
    };
  }, [currentMode, handleTextChunk, handleAudioChunk, handleTurnEnd]);

  return { handleTextChunk, handleAudioChunk, handleTurnEnd };
};
